/* ==========================================================================
   Bundle builder — U&S Clothing
   assets/bundle-builder.js

   Reads the member list printed by snippets/bundle-builder.liquid and walks
   the shopper through one screen per garment — mum's shirt, dad's shirt, each
   child, any trousers — then offers anything extra.

   WHERE THE SCREENS COME FROM
   Nothing is configured here. A bundle is a collection, its members are the
   garments the admin put in it, and the count for a repeated garment comes
   from the collection's bundle_counts. Four children means the kids tee
   appears four times with four separate screens.

   WHAT EACH SCREEN ASKS
   Read off the garment itself. A garment with print locations is a
   personalisable product, so its screen collects location, text or artwork,
   font and thread colour — the same inputs as the personalisation wizard,
   without the preview. Any other garment asks size and colour only. The admin
   never says which; putting the product in the bundle is enough.

   Deliberately no garment preview. These bundles run six or more people deep
   and a preview on every screen would only lengthen an already long journey.

   WHAT GOES IN THE CART
     line 1  the bundle product at its own price, with every garment's choices
             in its line item properties
     line n  one line per extra garment, at that garment's own variant price
     line n  one line per PAID print location, at the Additional Print
             Location price
     all of them tied together by _bundle_id / _bundle_of

   The split is not a style choice. A storefront cannot set a line item's
   price — price always comes from the variant — so anything that costs money
   has to be its own line. This is the same shape the personalisation wizard
   already uses, and the cart shows them together.
   ========================================================================== */
(function () {
  'use strict';

  /* main-product.liquid is re-fetched on variant change and its scripts are
     re-executed, so a second copy of this file must not stack another set of
     listeners. */
  if (window.__bundleBuilderLoaded) return;
  window.__bundleBuilderLoaded = true;

  var UPLOAD_MAX_PX = 3000;
  var UPLOAD_SKIP_BYTES = 4 * 1024 * 1024;
  var TEXT_MAX = 30;

  var cfg = null;          /* the whole config blob                    */
  var MEMBERS = [];        /* the bundle's garments, in order          */
  var THREADS = [];        /* thread colours, from theme settings      */
  var FONTS = [];          /* print fonts, from theme settings         */
  var products = {};       /* handle -> product JSON, cached           */
  var fontsLoaded = false;

  var els = {};
  var steps = [];
  var idx = 0;
  var busy = false;
  var editingExtra = null; /* index into state.extras, or null         */
  var pendingError = null;

  var state = { slots: {}, grids: {}, extras: [] };
  var gridColour = {};     /* handle -> the colour whose sizes are on screen */

  /* Not every garment has both options. A tote bag has colours and no sizes;
     something like a mug may have neither. The grid is keyed
     colour -> size -> count, so a missing option becomes this empty key and
     the shape stays the same — the tally, the review and the order
     properties all keep working without a second code path. Wherever it is
     read back, an empty key means "this garment has no such option" and is
     left out of what the shopper and the warehouse see. */
  var NO_OPT = '';

  /* ------------------------------------------------------------- helpers */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function money(cents) {
    var fmt = cfg.moneyFormat || '£{{amount}}';
    var n = (Number(cents) || 0) / 100;
    var s = n.toFixed(2);
    /* The theme's money format covers the common cases; anything exotic falls
       back to the plain number rather than printing a broken string. */
    if (fmt.indexOf('{{amount}}') > -1) return fmt.replace('{{amount}}', s);
    if (fmt.indexOf('{{amount_no_decimals}}') > -1) {
      return fmt.replace('{{amount_no_decimals}}', String(Math.round(n)));
    }
    if (fmt.indexOf('{{amount_with_comma_separator}}') > -1) {
      return fmt.replace('{{amount_with_comma_separator}}', s.replace('.', ','));
    }
    return '£' + s;
  }

  /* Shopify serves whatever size was uploaded, often 2000px wide. These
     thumbnails are drawn at about 150px, so the CDN is asked for a small one
     — six full-size garment photos on one screen is megabytes for nothing. */
  function thumb(url, px) {
    var u = String(url || '');
    if (!u) return '';
    if (u.indexOf('//') === 0) u = 'https:' + u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + (px || 300);
  }

  /* A native date input always hands back YYYY-MM-DD. Nobody here writes a
     date that way, and the print team reads these off the order, so it is
     turned round before it is shown or sent. */
  function ukDate(v) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
    return m ? m[3] + '/' + m[2] + '/' + m[1] : v;
  }

  /* Text can now carry line breaks. The cart and the order keep them, but the
     review and the added-so-far list are single lines of their own, so a break
     is shown as a slash rather than silently closing the gap. */
  function oneLine(v) {
    return String(v == null ? '' : v).replace(/\s*\n+\s*/g, ' / ').trim();
  }

  function isDateValue(v) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
  }

  /* The same fallback list the wizard and the inline personaliser carry.

     Theme settings -> Customisation -> Colour list is empty on this store, so
     without this the bundle asked for no print colour at all while the
     product page asked for one — the same combination would reach the print
     team with a colour from one route and nothing from the other. An empty
     setting and a switched-off feature look identical to the code, which is
     why both of the others already carry this list. */
  var DEFAULT_COLOURS = [
    'White:#FFFFFF','Mallard Teal:#16646B','Sky:#7FB9E0','Silver:#C8CBCC',
    'Sand:#E4D5BE','Rose Gold:#D9A08C','Red:#C8102E','Purple:#3E1F6D',
    'Purple Pansy:#7D3F98','Champagne:#E3D9B8','Black:#101010',
    'Pink:#F26FA2','Pale Blue:#3EB1E5','Orange:#E8631A','Olive Green:#2F3B15',
    'Cream:#F2ECD8','Nugget Gold:#E0A800','Neon Yellow:#F5E400','Navy:#14213D',
    'Mint:#3FBF9A','Brown:#6B3A17','Baby Pink:#F7C5D5',
    'Maroon:#6B0F1A','Magenta:#D6006E','Lilac:#8E7BC1','Light Green:#4CAF50',
    'Light Brown:#A9732F','Green:#159B4A','Light Blue:#2E9BD6','Grey:#9AA0A6',
    'Gold:#D9A520','Bright Pink:#FF3B4E','Autumn Orange:#E8781E',
    'Forest Green:#14512B','Fluorescent Lime:#8FE01A','Flamingo Pink:#FF1F8F',
    'Dark Grey:#4A4A4A','Dark Brown:#2E1A0F','Dark Gold:#C08A17',
    'Dark Blue:#123C7A','Charcoal:#2B2B2B','Bottle Green:#0F4436','Yellow:#F5D000'
  ];

  function parseColours(lines) {
    var out = [];
    (lines && lines.length ? lines : DEFAULT_COLOURS).forEach(function (raw) {
      if (!raw) return;
      var i = String(raw).lastIndexOf(':');
      if (i < 1) return;
      var name = String(raw).slice(0, i).trim();
      var hex = String(raw).slice(i + 1).trim();
      if (!name || !/^#?[0-9a-f]{3,8}$/i.test(hex)) return;
      if (hex[0] !== '#') hex = '#' + hex;
      out.push({ name: name, hex: hex });
    });
    return out.length ? out : parseColours(DEFAULT_COLOURS);
  }

  /* Same line format as the personalisation wizard, so one list in Theme
     settings serves both:
       Bebas Neue              a Google family
       Bebas=Bebas Neue:600    a Google family under a friendlier name
       Signature=sig.woff2     a file uploaded to the theme's assets   */
  function parseFonts(lines) {
    var out = [];
    (lines || []).forEach(function (raw) {
      if (!raw) return;
      var left = String(raw).split('|')[0].trim();
      if (!left) return;

      var eq = left.split('=');
      var spec = (eq.length > 1 ? eq[1] : eq[0]).trim();
      if (!spec) return;

      var bits = spec.split(':');
      var family = bits[0].trim();
      var weight = (bits[1] || '').trim();
      if (!family) return;

      var isFile = /\.(woff2|woff|ttf|otf)$/i.test(family);
      var display = eq.length > 1 ? eq[0].trim() : family;
      if (!display) return;

      out.push({
        name: display,
        file: isFile ? family : null,
        css: "'" + (isFile ? display : family) + "', sans-serif",
        google: isFile ? null : family.replace(/\s+/g, '+') + (weight ? ':wght@' + weight : '')
      });
    });
    return out;
  }

  /* Faces load when the modal first opens, never on page load — a bundle page
     nobody personalises should not pay for them. */
  function loadFonts() {
    if (fontsLoaded || !FONTS.length) return;
    fontsLoaded = true;

    var faces = [], google = [];
    FONTS.forEach(function (f) {
      if (f.file) {
        var fmt = /woff2$/i.test(f.file) ? 'woff2'
          : /woff$/i.test(f.file) ? 'woff'
            : /otf$/i.test(f.file) ? 'opentype' : 'truetype';
        faces.push("@font-face{font-family:'" + f.name + "';"
          + "src:url('" + (cfg.fontAssetBase || '') + f.file + "') format('" + fmt + "');"
          + 'font-display:swap;font-weight:normal;font-style:normal}');
      } else if (f.google) {
        google.push('family=' + f.google);
      }
    });

    if (faces.length) {
      var st = document.createElement('style');
      st.textContent = faces.join('\n');
      document.head.appendChild(st);
    }
    if (google.length) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?' + google.join('&') + '&display=swap';
      document.head.appendChild(link);
    }
  }

  /* The store's products name their options "Color" and "Size". Match both
     spellings of colour, and tolerate an admin writing "Colour" one day. */
  function optionIndex(product, kind) {
    var re = kind === 'size' ? /size/i : /colou?r/i;
    var names = (product && product.options) || [];
    for (var i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : names[i].name;
      if (re.test(n)) return i;
    }
    return -1;
  }

  function optionValues(product, kind, filterBy) {
    var i = optionIndex(product, kind);
    if (i < 0) return [];
    var seen = {}, out = [];
    (product.variants || []).forEach(function (v) {
      /* When a colour is already chosen, only offer sizes that exist in it —
         otherwise the shopper picks a combination with no variant behind it
         and the garment cannot be priced. */
      if (filterBy) {
        for (var k in filterBy) {
          if (!Object.prototype.hasOwnProperty.call(filterBy, k)) continue;
          var fi = optionIndex(product, k);
          if (fi >= 0 && v.options[fi] !== filterBy[k]) return;
        }
      }
      var val = v.options[i];
      if (val == null || seen[val]) return;
      seen[val] = true;
      out.push({ value: val, image: v.featured_image ? v.featured_image.src : null });
    });
    return out;
  }

  function findVariant(product, opts) {
    var ci = optionIndex(product, 'colour');
    var si = optionIndex(product, 'size');
    var list = (product && product.variants) || [];
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      if (ci >= 0 && opts.colour != null && v.options[ci] !== opts.colour) continue;
      if (si >= 0 && opts.size != null && v.options[si] !== opts.size) continue;
      return v;
    }
    return null;
  }

  function fetchProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(products, handle)) {
      return Promise.resolve(products[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(function (p) { products[handle] = p; return p; })
      .catch(function () { products[handle] = null; return null; });
  }

  function memberByHandle(handle) {
    for (var i = 0; i < MEMBERS.length; i++) {
      if (MEMBERS[i].handle === handle) return MEMBERS[i];
    }
    return null;
  }

  /* ------------------------------------------------------------- stepping */

  function memberQty(m) {
    return Math.max(1, Number(m.qty) || 1);
  }

  /* Two shapes, and the admin picks which by how the count was written.

       kids-tee: 4 as Child    four people, four screens — Child 1 … Child 4,
                               each with its own size and its own name
       polo-shirt: 10          ten of the same thing — one screen with a size
                               grid, and the questions asked once for all ten

     A workwear bundle of ten polos through the first shape would be ten
     screens of identical answers, which nobody fills in. A family bundle
     through the second could not hold four different children's names. So
     both exist, and naming the items is what says they are individuals. */
  function buildSteps() {
    steps = [];
    MEMBERS.forEach(function (m) {
      var qty = memberQty(m);

      if (qty === 1) {
        steps.push({ kind: 'slot', member: m, key: m.handle + '#1', label: m.label });
        return;
      }

      if (m.named) {
        for (var n = 1; n <= qty; n++) {
          steps.push({
            kind: 'slot',
            member: m,
            key: m.handle + '#' + n,
            label: m.label + ' ' + n
          });
        }
        return;
      }

      /* Colours and sizes on one screen, then the questions on the next, so
         neither screen needs scrolling on a phone. */
      steps.push({ kind: 'grid', member: m, key: m.handle, label: m.label });
      if (hasPersonalisation(m)) {
        steps.push({ kind: 'custom', member: m, key: m.handle, label: m.label });
      }
    });

    /* The extras pool is the bundle's own members, so it is offered whenever
       the bundle has anything in it at all. */
    steps.push({ kind: 'extras' });
    steps.push({ kind: 'review' });
  }

  /* The garment's handle is kept on the slot's own state so a row in the
     review or a line in the cart can find its member — and therefore its
     print location labels — from the data alone. */
  function slotState(key) {
    if (!state.slots[key]) {
      var member = null;
      steps.forEach(function (st) { if (st.kind === 'slot' && st.key === key) member = st.member; });
      state.slots[key] = {
        handle: member ? member.handle : null,
        opts: {}, fields: {}, print: {}
      };
    }
    return state.slots[key];
  }

  /* A grid garment holds one entry for the whole run rather than one per item:
     picks is colour -> size -> how many, and the questions are answered once.

       picks: { Black: { M: 3, L: 4 }, Navy: { L: 3 } }   */
  function gridState(member) {
    if (!state.grids[member.handle]) {
      state.grids[member.handle] = {
        handle: member.handle, picks: {}, fields: {}, print: {}
      };
    }
    return state.grids[member.handle];
  }

  function gridCount(data, colour) {
    var n = 0;
    Object.keys(data.picks || {}).forEach(function (c) {
      if (colour && c !== colour) return;
      Object.keys(data.picks[c]).forEach(function (sz) { n += data.picks[c][sz] || 0; });
    });
    return n;
  }

  function setPick(data, colour, size, n) {
    if (!data.picks[colour]) data.picks[colour] = {};
    if (n > 0) data.picks[colour][size] = n;
    else {
      delete data.picks[colour][size];
      /* An emptied colour is dropped, so its badge disappears and it does not
         show up as a colour with nothing in it on the review. */
      if (!Object.keys(data.picks[colour]).length) delete data.picks[colour];
    }
  }

  /* ------------------------------------------------------------- pricing */

  /* Paid print locations are charged on top, exactly as the wizard charges
     them, and only for locations the shopper actually chose. */
  function printPrice(member, data) {
    if (!member || !member.print) return 0;
    var t = 0;
    (member.print.locations || []).forEach(function (l) {
      var d = data.print && data.print[l.key];
      if (d) t += Number(l.price) || 0;
    });
    return t;
  }

  function extraPrice(x) {
    var m = memberByHandle(x.handle);
    var base = x.variant ? Number(x.variant.price) || 0 : 0;
    return base + printPrice(m, x);
  }

  function total() {
    var t = Number(cfg.basePrice) || 0;
    /* A garment included in the bundle costs nothing extra, but choosing a
       paid print location on it does — and on a run of ten garments that
       print is paid for ten times, not once. */
    steps.forEach(function (st) {
      if (st.kind === 'slot') {
        t += printPrice(st.member, slotState(st.key));
      } else if (st.kind === 'grid') {
        t += printPrice(st.member, gridState(st.member)) * memberQty(st.member);
      }
    });
    state.extras.forEach(function (x) { t += extraPrice(x); });
    return t;
  }

  /* --------------------------------------------------------- field pieces */

  function fieldWrap(kind, labelText, note) {
    var wrap = document.createElement('div');
    wrap.className = 'bb-field';
    wrap.setAttribute('data-field', kind);
    if (labelText) wrap.innerHTML = '<label>' + esc(labelText) + '</label>';
    if (note) wrap.innerHTML += '<p class="bb-field__note">' + esc(note) + '</p>';
    return wrap;
  }

  function buildColour(product, target, onChange) {
    var vals = optionValues(product, 'colour');
    if (!vals.length) return null;
    var wrap = fieldWrap('colour', 'Colour');
    var box = document.createElement('div');
    box.className = 'bb-swatches';
    vals.forEach(function (c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-swatch' + (target.opts.colour === c.value ? ' is-on' : '');
      b.innerHTML = (c.image ? '<img src="' + esc(c.image) + '" alt="" loading="lazy">' : '')
        + '<span>' + esc(c.value) + '</span>';
      b.addEventListener('click', function () {
        target.opts.colour = c.value;
        /* Sizes depend on the colour, so the screen is rebuilt rather than
           patched — simpler than reconciling two lists by hand. */
        if (target.opts.size) {
          var still = optionValues(product, 'size', { colour: c.value })
            .some(function (s) { return s.value === target.opts.size; });
          if (!still) delete target.opts.size;
        }
        onChange();
      });
      box.appendChild(b);
    });
    wrap.appendChild(box);
    return wrap;
  }

  function buildSize(product, target, onChange) {
    var filter = target.opts.colour ? { colour: target.opts.colour } : null;
    var vals = optionValues(product, 'size', filter);
    if (!vals.length) return null;
    var wrap = fieldWrap('size', 'Size');
    var box = document.createElement('div');
    box.className = 'bb-choices';
    vals.forEach(function (s) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-chip' + (target.opts.size === s.value ? ' is-on' : '');
      b.textContent = s.value;
      b.addEventListener('click', function () {
        target.opts.size = s.value;
        onChange();
      });
      box.appendChild(b);
    });
    wrap.appendChild(box);
    return wrap;
  }

  function buildChips(labelText, values, current, onPick, styleEach, kind) {
    var wrap = fieldWrap(kind || 'chips', labelText);
    var box = document.createElement('div');
    box.className = 'bb-choices';
    values.forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-chip' + (current === v.value ? ' is-on' : '');
      b.textContent = v.label;
      if (styleEach) styleEach(b, v);
      b.addEventListener('click', function () { onPick(v.value); });
      box.appendChild(b);
    });
    wrap.appendChild(box);
    return wrap;
  }

  function buildThreads(labelText, current, onPick) {
    if (!THREADS.length) return null;
    var wrap = fieldWrap('thread', labelText);
    var box = document.createElement('div');
    box.className = 'bb-threads';
    THREADS.forEach(function (c) {
      var holder = document.createElement('div');
      holder.className = 'bb-thread-wrap';
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-thread' + (current === c.name ? ' is-on' : '');
      b.style.background = c.hex;
      b.setAttribute('aria-label', c.name);
      b.addEventListener('click', function () { onPick(c.name); });
      holder.appendChild(b);
      var nm = document.createElement('span');
      nm.className = 'bb-thread__name';
      nm.textContent = c.name;
      holder.appendChild(nm);
      box.appendChild(holder);
    });
    wrap.appendChild(box);
    return wrap;
  }

  /* A textarea, not an input, so Enter puts in a line break.

     Shoppers kept typing a name and a date meaning them to print on two
     lines, and a single-line input silently ran them together. The print team
     then had to guess. A textarea keeps the break, the cart carries it, and
     the order shows it.

     It grows with what is typed rather than scrolling inside itself: a box
     with hidden text in it is easy to miss on a phone. */
  function buildText(labelText, current, onInput, max) {
    var limit = Number(max) > 0 ? Number(max) : TEXT_MAX;
    var wrap = fieldWrap('text', labelText);

    var inp = document.createElement('textarea');
    inp.className = 'bb-input bb-input--multi';
    inp.rows = 2;
    inp.maxLength = limit;
    inp.value = current || '';

    var count = document.createElement('span');
    count.className = 'bb-count';

    function grow() {
      inp.style.height = 'auto';
      inp.style.height = inp.scrollHeight + 'px';
    }
    function tick() {
      count.textContent = inp.value.length + '/' + limit;
    }

    tick();
    inp.addEventListener('input', function () {
      onInput(inp.value);
      tick();
      grow();
      els.err.textContent = '';
    });
    /* The field is built before it is in the document, so the first measure
       has to wait for layout or scrollHeight reads as zero. */
    setTimeout(grow, 0);

    wrap.appendChild(inp);
    wrap.appendChild(count);
    return wrap;
  }

  function buildUpload(labelText, current, onDone) {
    var wrap = fieldWrap('image', labelText);

    var drop = document.createElement('div');
    drop.className = 'bb-drop' + (current ? ' is-filled' : '');
    drop.innerHTML = current
      ? '<img src="' + esc(current) + '" alt=""><strong>Image added</strong><small>Tap to replace</small>'
      : '<strong>Choose an image</strong><small>JPG, PNG or WEBP &middot; at least 1000px wide for a sharp print</small>';

    var note = document.createElement('div');
    note.className = 'bb-progress';
    note.hidden = true;
    note.innerHTML = '<div class="bb-progress__bar"><i></i></div>'
      + '<p class="bb-progress__label">Uploading&hellip;</p>';

    var file = document.createElement('input');
    file.type = 'file';
    file.accept = 'image/*';
    file.hidden = true;

    drop.addEventListener('click', function () { file.click(); });

    file.addEventListener('change', function () {
      var fl = file.files[0];
      if (!fl) return;
      var bar = note.querySelector('.bb-progress__bar i');
      var lbl = note.querySelector('.bb-progress__label');
      note.hidden = false;
      bar.style.width = '0%';
      lbl.textContent = 'Preparing image…';
      startUpload('Preparing image…');

      uploadImage(fl, function (pct) {
        var msg;
        if (pct === null) { bar.style.width = '100%'; msg = 'Uploading…'; }
        else {
          bar.style.width = pct + '%';
          msg = pct < 100 ? 'Uploading… ' + pct + '%' : 'Finishing up…';
        }
        lbl.textContent = msg;
        setUploadLabel(msg);
      }).then(function (url) {
        note.hidden = true;
        endUpload();
        onDone(url);
      }).catch(function () {
        note.hidden = true;
        endUpload();
        els.err.textContent = 'That image did not upload — tap the box to try again. '
          + 'If it keeps failing, try a different photo or a stronger connection.';
        render();
      });
    });

    wrap.appendChild(drop);
    wrap.appendChild(note);
    wrap.appendChild(file);
    return wrap;
  }

  /* ---------------------------------------------------------- print block */

  function availableLocations(member) {
    if (!member || !member.print) return [];
    return (member.print.locations || []).filter(function (l) { return l.available; });
  }

  function usesFrontPositions(member, loc) {
    return /front/i.test(loc.label || '')
      && member.print.frontPositions
      && member.print.frontPositions.length > 0;
  }

  /* Everything the personalisation wizard asks for, minus the preview: which
     locations, then per location a position, text or artwork, font and thread
     colour. Driven entirely by the garment's own metafields. */
  function buildPrintBlock(member, target, onChange) {
    var locs = availableLocations(member);
    if (!locs.length) return null;
    if (!target.print) target.print = {};

    var box = document.createElement('div');
    box.className = 'bb-print';

    var pick = fieldWrap('locations', 'Where would you like it printed?',
      'Choose one or more.');
    var row = document.createElement('div');
    row.className = 'bb-choices';
    locs.forEach(function (l) {
      var on = !!target.print[l.key];
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-chip bb-chip--wide' + (on ? ' is-on' : '');
      b.innerHTML = '<span>' + esc(l.label) + '</span>'
        + '<small>' + (Number(l.price) > 0 ? '+ ' + esc(money(l.price)) : 'Included') + '</small>';
      b.addEventListener('click', function () {
        if (target.print[l.key]) delete target.print[l.key];
        else target.print[l.key] = defaultPrintData(member);
        onChange();
      });
      row.appendChild(b);
    });
    pick.appendChild(row);
    box.appendChild(pick);

    /* A panel per chosen location, in the garment's own location order. */
    locs.forEach(function (l) {
      var d = target.print[l.key];
      if (!d) return;
      box.appendChild(buildPrintPanel(member, l, d, onChange));
    });

    return box;
  }

  /* When only one of text or image is allowed there is nothing to choose, so
     the mode is set here and the toggle never appears. */
  function defaultPrintData(member) {
    var pr = member.print;
    var mode = null;
    if (pr.allowText && !pr.allowImage) mode = 'text';
    if (pr.allowImage && !pr.allowText) mode = 'image';
    return { mode: mode };
  }

  function buildPrintPanel(member, loc, d, onChange) {
    var pr = member.print;
    var panel = document.createElement('div');
    panel.className = 'bb-print__panel';

    var head = document.createElement('p');
    head.className = 'bb-print__title';
    head.textContent = loc.label;
    panel.appendChild(head);

    if (usesFrontPositions(member, loc)) {
      panel.appendChild(buildChips(
        'Position',
        pr.frontPositions.map(function (p) { return { value: p, label: p }; }),
        d.position,
        function (v) { d.position = v; onChange(); }
      ));
    }

    if (pr.allowText && pr.allowImage) {
      panel.appendChild(buildChips(
        'What would you like printed?',
        [{ value: 'text', label: 'Text' }, { value: 'image', label: 'An image' }],
        d.mode,
        function (v) { d.mode = v; onChange(); }
      ));
    }

    if (d.mode === 'text') {
      panel.appendChild(buildText('Your text', d.text, function (v) { d.text = v; }));

      if (FONTS.length) {
        panel.appendChild(buildChips(
          'Font',
          FONTS.map(function (f) { return { value: f.name, label: f.name, css: f.css }; }),
          d.font,
          function (v) { d.font = v; onChange(); },
          function (btn, v) { btn.style.fontFamily = v.css; },
          'fonts'
        ));
      }

      var th = buildThreads('Thread colour', d.thread, function (v) {
        d.thread = v; onChange();
      });
      if (th) panel.appendChild(th);
    }

    if (d.mode === 'image') {
      panel.appendChild(buildUpload('Your image', d.image, function (url) {
        d.image = url; onChange();
      }));
    }

    return panel;
  }

  /* ------------------------------------------- the inline personaliser's own

     A garment can be set up for personalisation in either of two ways, and a
     bundle has to honour both:

       custom.print_locations    the wizard — locations, artwork, thread
       custom.inline_personalise a plain list the admin writes by hand

     The second reads one line per question, exactly as the inline personaliser
     parses it, so one setting feeds the product page and the bundle alike:

         Text
         Text:Enter Date
         Text:Enter Your Name:20
         Font
         Colour
         Image
         Position

     After the first colon is the shopper's label, which also becomes the name
     on the order. After a second colon, a character limit.
     ------------------------------------------------------------------------ */

  function parseWants(lines) {
    var out = [];
    (lines || []).forEach(function (raw) {
      if (!raw) return;
      var bits = String(raw).split(':');
      var kind = (bits[0] || '').trim().toLowerCase();
      if (!kind) return;
      var limit = parseInt((bits[2] || '').trim(), 10);
      out.push({
        kind: kind,
        label: (bits[1] || '').trim(),
        max: limit > 0 ? limit : 40
      });
    });
    return out;
  }

  function wantsOf(member, kind) {
    if (!member.inline) return [];
    return parseWants(member.inline.wants).filter(function (w) { return w.kind === kind; });
  }

  /* "Colour" in this list is the colour of the print, not of the garment — the
     garment's own colour comes from its variants. They are kept apart on the
     order too: "Font colour" against plain "Colour". */
  function appendInlineFields(container, member, target, onChange) {
    var any = false;

    wantsOf(member, 'text').forEach(function (w) {
      var key = w.label || 'Text';
      container.appendChild(buildText(key, target.fields[key], function (v) {
        target.fields[key] = v;
      }, w.max));
      any = true;
    });

    var fontWant = wantsOf(member, 'font')[0];
    if (fontWant && FONTS.length) {
      var fkey = fontWant.label || 'Font';
      if (!target.fields[fkey]) target.fields[fkey] = FONTS[0].name;
      container.appendChild(buildChips(
        fkey,
        FONTS.map(function (f) { return { value: f.name, label: f.name, css: f.css }; }),
        target.fields[fkey],
        function (v) { target.fields[fkey] = v; onChange(); },
        function (btn, v) { btn.style.fontFamily = v.css; },
        'fonts'
      ));
      any = true;
    }

    var colourWant = wantsOf(member, 'colour')[0];
    if (colourWant && THREADS.length) {
      var ckey = colourWant.label || 'Font colour';
      if (!target.fields[ckey]) {
        var black = THREADS.filter(function (c) { return c.name === 'Black'; })[0];
        target.fields[ckey] = (black || THREADS[0]).name;
      }
      var cel = buildThreads(ckey, target.fields[ckey], function (v) {
        target.fields[ckey] = v; onChange();
      });
      if (cel) { container.appendChild(cel); any = true; }
    }

    var posWant = wantsOf(member, 'position')[0];
    var positions = (member.inline && member.inline.positions) || [];
    if (posWant && positions.length) {
      var pkey = posWant.label || 'Position';
      if (!target.fields[pkey]) target.fields[pkey] = positions[0];
      container.appendChild(buildChips(
        pkey,
        positions.map(function (p) { return { value: p, label: p }; }),
        target.fields[pkey],
        function (v) { target.fields[pkey] = v; onChange(); }
      ));
      any = true;
    }

    /* A product already using the theme's own upload block keeps it — a second
       uploader would give the shopper two boxes for one image. */
    var imgWant = wantsOf(member, 'image')[0];
    if (imgWant && !(member.inline && member.inline.hasOldUploader)) {
      var ikey = imgWant.label || 'Image';
      container.appendChild(buildUpload(ikey, target.fields[ikey], function (url) {
        target.fields[ikey] = url; onChange();
      }));
      any = true;
    }

    return any;
  }

  function hasPersonalisation(member) {
    if (member.print && availableLocations(member).length) return true;
    return !!(member.inline && (member.inline.wants || []).length);
  }

  /* ----------------------------------------------------- garment assembly */

  /* Shared by an included garment's screen and an extra's screen, so the two
     can never drift apart. */
  function appendGarment(container, member, product, target, onChange) {
    var c = buildColour(product, target, onChange);
    if (c) container.appendChild(c);

    var s = buildSize(product, target, onChange);
    if (s) container.appendChild(s);

    appendPersonalisation(container, member, target, onChange);
  }

  /* Just the questions, without size and colour — the grid screen asks those
     separately and then asks these once for the whole run. */
  function appendPersonalisation(container, member, target, onChange) {
    if (member.print) {
      var p = buildPrintBlock(member, target, onChange);
      if (p) container.appendChild(p);
    }
    if (member.inline) appendInlineFields(container, member, target, onChange);
  }

  /* --------------------------------------------------- upload button state */

  /* Reported from session recordings on the personalisation wizard: shoppers
     tapped Next over and over during an upload and nothing happened, because
     validation refused to move on while the artwork had no URL yet. The
     button now says what it is doing and cannot be pressed. */
  var uploadState = { active: false, label: '' };

  function applyUploadUI() {
    if (!uploadState.active || busy) return;
    els.next.disabled = true;
    els.next.textContent = uploadState.label;
  }
  function startUpload(label) { uploadState = { active: true, label: label }; applyUploadUI(); }
  function setUploadLabel(t) { uploadState.label = t; applyUploadUI(); }
  function endUpload() { uploadState = { active: false, label: '' }; }

  /* ------------------------------------------------------------- uploading */

  function prepare(file) {
    return new Promise(function (resolve) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var big = Math.max(img.width, img.height);
          /* Already a sensible size — send the shopper's own file untouched.
             Re-encoding something that is fine can only lose quality. */
          if (big <= UPLOAD_MAX_PX && file.size <= UPLOAD_SKIP_BYTES) {
            URL.revokeObjectURL(url); resolve(file); return;
          }
          var scale = Math.min(1, UPLOAD_MAX_PX / big);
          var c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(img.width * scale));
          c.height = Math.max(1, Math.round(img.height * scale));
          var ctx = c.getContext('2d');
          /* PNG and WEBP keep their format: flattening a logo onto white means
             a white box printed behind it on a dark garment. */
          var keepAlpha = /png|webp/i.test(file.type || '');
          if (!keepAlpha) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, c.width, c.height);
          }
          ctx.drawImage(img, 0, 0, c.width, c.height);
          URL.revokeObjectURL(url);
          var type = keepAlpha ? 'image/png' : 'image/jpeg';
          var name = keepAlpha ? 'artwork.png' : 'artwork.jpg';
          c.toBlob(function (blob) {
            if (!blob || blob.size >= file.size) { resolve(file); return; }
            resolve(new File([blob], name, { type: type }));
          }, type, 0.88);
        } catch (e) {
          URL.revokeObjectURL(url); resolve(file);
        }
      };
      /* HEIC and anything else a canvas cannot read goes as it is; the server
         sorts it out. */
      img.onerror = function () { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  function post(file, onProgress) {
    return new Promise(function (resolve, reject) {
      var fd = new FormData();
      fd.append('image', file);
      var xhr = new XMLHttpRequest();
      xhr.open('POST', cfg.uploadUrl);
      xhr.upload.onprogress = function (e) {
        if (!onProgress) return;
        onProgress(e.lengthComputable ? Math.round((e.loaded / e.total) * 100) : null);
      };
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) { data = null; }
        if (xhr.status >= 200 && xhr.status < 300 && data && data.success && data.url) {
          if (onProgress) onProgress(100);
          resolve(data.url);
        } else {
          reject(new Error((data && data.error) || 'upload rejected'));
        }
      };
      xhr.onerror = function () { reject(new Error('network error')); };
      xhr.ontimeout = function () { reject(new Error('timed out')); };
      xhr.timeout = 60000;
      xhr.send(fd);
    });
  }

  function uploadImage(file, onProgress) {
    return prepare(file).then(function (ready) {
      return post(ready, onProgress).catch(function () {
        /* One retry. A dropped connection on mobile data is common enough that
           making the shopper find the photo again is not reasonable. */
        if (onProgress) onProgress(0);
        return post(ready, onProgress);
      });
    });
  }

  /* -------------------------------------------------------------- screens */

  function renderSlot(step) {
    var member = step.member;
    var st = slotState(step.key);
    var product = products[member.handle];

    els.sub.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
    els.body.innerHTML = '';

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = step.label;
    els.body.appendChild(h);

    if (!product) {
      var miss = document.createElement('p');
      miss.className = 'bb-step-help';
      miss.textContent = 'This item could not be loaded. Please refresh the page and try again.';
      els.body.appendChild(miss);
      return;
    }

    /* The garment's own name, when the step is called something else. */
    if (step.label !== product.title) {
      var sub = document.createElement('p');
      sub.className = 'bb-step-help';
      sub.textContent = product.title;
      els.body.appendChild(sub);
    }

    appendGarment(els.body, member, product, st, render);
    els.next.textContent = 'Next';
  }

  /* The grid screen.

     One garment, a fixed number of them, and the shopper decides how they are
     split. Colours carry a count badge and only one colour's sizes are on
     screen at a time, so ten colours still fit without scrolling — a full
     colour-by-size grid would not come close on a phone.

     Picking the same colour for everything is the common case and costs one
     tap; mixing four colours works the same way, one colour at a time. */
  function renderGrid(step) {
    var member = step.member;
    var data = gridState(member);
    var product = products[member.handle];
    var want = memberQty(member);

    els.sub.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
    els.body.innerHTML = '';

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = step.label;
    els.body.appendChild(h);

    if (!product) {
      els.body.innerHTML += '<p class="bb-step-help">This item could not be loaded. '
        + 'Please refresh the page and try again.</p>';
      return;
    }

    var have = gridCount(data);
    var tally = document.createElement('p');
    tally.className = 'bb-tally' + (have === want ? ' is-done' : '');
    tally.textContent = have + ' of ' + want + ' chosen';
    els.body.appendChild(tally);

    var colours = optionValues(product, 'colour');
    var active = gridColour[member.handle];
    if (!colours.length) active = NO_OPT;
    if (colours.length) {
      if (!active || !colours.some(function (c) { return c.value === active; })) {
        /* Start on a colour the shopper has already put something into, so
           coming back to this screen lands where they left off. */
        var used = Object.keys(data.picks)[0];
        active = used || colours[0].value;
        gridColour[member.handle] = active;
      }

      var cw = fieldWrap('colour', 'Colour');
      var cbox = document.createElement('div');
      cbox.className = 'bb-swatches';
      colours.forEach(function (c) {
        var n = gridCount(data, c.value);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bb-swatch' + (active === c.value ? ' is-on' : '')
          + (n > 0 ? ' has-count' : '');
        b.innerHTML = (c.image ? '<img src="' + esc(thumb(c.image, 200)) + '" alt="" loading="lazy">' : '')
          + '<span>' + esc(c.value) + '</span>'
          + (n > 0 ? '<i class="bb-swatch__count">' + n + '</i>' : '');
        b.addEventListener('click', function () {
          gridColour[member.handle] = c.value;
          render();
        });
        cbox.appendChild(b);
      });
      cw.appendChild(cbox);
      els.body.appendChild(cw);
    }

    /* A garment with no Size option at all still has to be counted. Without
       this it fell through to "No sizes available in that colour" and there
       was nothing on screen to tap, so Next never unlocked and the whole
       bundle stopped on that step. It gets one counter per colour instead of
       one per size. */
    var hasSize = optionIndex(product, 'size') >= 0;
    var sizes = hasSize
      ? optionValues(product, 'size', active ? { colour: active } : null)
      : [{ value: NO_OPT }];

    if (hasSize && !sizes.length) {
      els.body.innerHTML += '<p class="bb-step-help">No sizes available in that colour.</p>';
      return;
    }

    var sw = fieldWrap('qty', hasSize
      ? (active ? 'Sizes in ' + active : 'Sizes')
      : 'How many');
    var rows = document.createElement('div');
    rows.className = 'bb-qty';

    sizes.forEach(function (szv) {
      var sz = szv.value;
      var n = (data.picks[active] && data.picks[active][sz]) || 0;

      var row = document.createElement('div');
      row.className = 'bb-qty__row' + (n > 0 ? ' is-on' : '');

      /* With no size to name the row, the colour names it instead, so the
         counter is never an unlabelled pair of buttons. */
      var rowName = sz || active || 'Quantity';

      var name = document.createElement('span');
      name.className = 'bb-qty__size';
      name.textContent = rowName;
      row.appendChild(name);

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'bb-qty__btn';
      minus.textContent = '−';
      minus.disabled = n <= 0;
      minus.setAttribute('aria-label', 'One less ' + rowName);
      minus.addEventListener('click', function () {
        setPick(data, active, sz, n - 1);
        render();
      });

      var out = document.createElement('span');
      out.className = 'bb-qty__n';
      out.textContent = n;

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'bb-qty__btn';
      plus.textContent = '+';
      /* Nothing can be added past the number the bundle is sold as, so the
         shopper cannot reach the end and be told to go back and take some
         out again. */
      plus.disabled = gridCount(data) >= want;
      plus.setAttribute('aria-label', 'One more ' + rowName);
      plus.addEventListener('click', function () {
        setPick(data, active, sz, n + 1);
        render();
      });

      row.appendChild(minus);
      row.appendChild(out);
      row.appendChild(plus);
      rows.appendChild(row);
    });

    sw.appendChild(rows);
    els.body.appendChild(sw);

    els.next.textContent = 'Next';
  }

  /* The questions for a grid garment, asked once for the whole run. */
  function renderCustom(step) {
    var member = step.member;
    var data = gridState(member);

    els.sub.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
    els.body.innerHTML = '';

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = step.label;
    els.body.appendChild(h);

    var help = document.createElement('p');
    help.className = 'bb-step-help';
    help.textContent = 'This applies to all ' + memberQty(member) + '.';
    els.body.appendChild(help);

    appendPersonalisation(els.body, member, data, render);
    els.next.textContent = 'Next';
  }

  function renderExtras() {
    els.sub.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
    els.body.innerHTML = '';

    /* Filling in one extra takes over this same step, so the step count stays
       honest no matter how many people are added. */
    if (editingExtra !== null) { renderExtraEdit(); return; }

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = 'Anyone else to add?';
    els.body.appendChild(h);

    var help = document.createElement('p');
    help.className = 'bb-step-help';
    help.textContent = 'Add as many extra items as you like, or carry on.';
    els.body.appendChild(help);

    var grid = document.createElement('div');
    grid.className = 'bb-addons';

    /* The pool is the bundle's own members. A shirts-only bundle offers extra
       shirts; a shirts-and-trousers bundle offers both; a trousers-only bundle
       offers trousers. Nothing separate to configure. */
    MEMBERS.forEach(function (m) {
      var p = products[m.handle];
      if (!p) return;
      var from = 0;
      (p.variants || []).forEach(function (v, i) {
        if (i === 0 || v.price < from) from = v.price;
      });
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-addon';
      /* product.js gives featured_image as a plain URL string, unlike a
         variant's, which is an object. */
      var photo = p.featured_image || (p.images && p.images[0]) || null;
      b.innerHTML = (photo
        ? '<img class="bb-addon__img" src="' + esc(thumb(photo)) + '" alt="" loading="lazy">'
        : '')
        + '<span class="bb-addon__label">' + esc(p.title) + '</span>'
        + '<span class="bb-addon__price">+ ' + esc(money(from)) + '</span>';
      b.addEventListener('click', function () {
        state.extras.push({ handle: m.handle, opts: {}, fields: {}, print: {}, variant: null });
        editingExtra = state.extras.length - 1;
        render();
      });
      grid.appendChild(b);
    });
    els.body.appendChild(grid);

    if (state.extras.length) {
      var box = document.createElement('div');
      box.className = 'bb-added';
      box.innerHTML = '<p class="bb-added__title">Added so far</p>';
      var ul = document.createElement('ul');
      ul.className = 'bb-added__list';

      state.extras.forEach(function (x, i) {
        var p = products[x.handle];
        var li = document.createElement('li');
        li.className = 'bb-added__item';

        var label = document.createElement('span');
        var bits = summaryBits(x);
        label.textContent = (p ? p.title : 'Item')
          + (bits.length ? ' — ' + bits.join(', ') : '')
          + ' · ' + money(extraPrice(x));
        li.appendChild(label);

        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'bb-added__remove';
        rm.setAttribute('aria-label', 'Remove');
        rm.textContent = '✕';
        rm.addEventListener('click', function () {
          state.extras.splice(i, 1);
          render();
        });
        li.appendChild(rm);
        ul.appendChild(li);
      });

      box.appendChild(ul);
      els.body.appendChild(box);
    }

    els.next.textContent = 'Next';
  }

  function renderExtraEdit() {
    var x = state.extras[editingExtra];
    var member = memberByHandle(x.handle);
    var product = products[x.handle];

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = product ? product.title : 'Extra item';
    els.body.appendChild(h);

    if (!product || !member) {
      els.body.innerHTML += '<p class="bb-step-help">This item could not be loaded.</p>';
      return;
    }

    appendGarment(els.body, member, product, x, render);

    /* Keep the running price honest as soon as a size and colour are known. */
    x.variant = findVariant(product, x.opts);

    els.next.textContent = 'Save';
  }

  function renderReview() {
    els.sub.textContent = 'Last step';
    els.body.innerHTML = '';

    var h = document.createElement('p');
    h.className = 'bb-step-title';
    h.textContent = 'Check your bundle';
    els.body.appendChild(h);

    var ul = document.createElement('ul');
    ul.className = 'bb-review';

    steps.forEach(function (st, i) {
      if (st.kind === 'slot') {
        var data = slotState(st.key);
        var extra = printPrice(st.member, data);
        ul.appendChild(reviewRow(
          st.label, data, i,
          extra > 0 ? 'Included + ' + money(extra) : 'Included'
        ));
        return;
      }
      if (st.kind === 'grid') {
        var g = gridState(st.member);
        var qty = memberQty(st.member);
        var ex = printPrice(st.member, g) * qty;
        ul.appendChild(reviewRow(
          st.label + ' × ' + qty, g, i,
          ex > 0 ? 'Included + ' + money(ex) : 'Included'
        ));
      }
    });

    /* Extras are edited on the extras step rather than in place, so their Edit
       link goes back there — where they can also be removed. */
    var extrasStep = -1;
    steps.forEach(function (st, i) { if (st.kind === 'extras') extrasStep = i; });

    state.extras.forEach(function (x) {
      var p = products[x.handle];
      ul.appendChild(reviewRow(
        p ? p.title : 'Extra item', x,
        extrasStep >= 0 ? extrasStep : null,
        money(extraPrice(x))
      ));
    });

    els.body.appendChild(ul);

    var tot = document.createElement('div');
    tot.className = 'bb-review__total';
    tot.innerHTML = '<span>Total</span><span>' + esc(money(total())) + '</span>';
    els.body.appendChild(tot);

    els.next.textContent = 'Add to cart';
  }

  /* One short line describing what has been chosen, used on the extras list
     and in the review. */
  function summaryBits(data) {
    var bits = [];
    if (data.opts && data.opts.size) bits.push(data.opts.size);
    if (data.opts && data.opts.colour) bits.push(data.opts.colour);
    splitLines(data).forEach(function (line) { bits.push(line); });

    Object.keys(data.print || {}).forEach(function (k) {
      var d = data.print[k];
      var label = printLabel(data, k) || k;
      if (d.mode === 'text' && d.text) bits.push(label + ': "' + oneLine(d.text) + '"');
      else if (d.mode === 'image' && d.image) bits.push(label + ': image');
      else bits.push(label);
    });

    Object.keys(data.fields || {}).forEach(function (k) {
      var v = data.fields[k];
      if (v && !/^https?:/i.test(v)) bits.push(isDateValue(v) ? ukDate(v) : oneLine(v));
    });
    return bits;
  }

  /* "Black M x3, L x4" — one line per colour, which is how the print team
     reads it off the order and how the review shows it. A garment with no
     sizes reads "Black x3", and one with neither option just "x3": an empty
     key is a missing option, so it is dropped rather than printed as a gap. */
  function splitLines(data) {
    if (!data || !data.picks) return [];
    return Object.keys(data.picks).map(function (c) {
      var sizes = Object.keys(data.picks[c]).map(function (sz) {
        var n = data.picks[c][sz];
        return sz ? sz + ' x' + n : 'x' + n;
      });
      return (c ? c + ' ' : '') + sizes.join(', ');
    });
  }

  function reviewRow(name, data, jumpTo, priceLabel) {
    var li = document.createElement('li');
    li.className = 'bb-review__row';

    var head = document.createElement('div');
    head.className = 'bb-review__head';
    head.innerHTML = '<span class="bb-review__name">' + esc(name) + '</span>'
      + '<span class="bb-review__price">' + esc(priceLabel) + '</span>';
    li.appendChild(head);

    var bits = [];
    if (data.opts && data.opts.size) bits.push('Size ' + data.opts.size);
    if (data.opts && data.opts.colour) bits.push(data.opts.colour);
    splitLines(data).forEach(function (line) { bits.push(line); });

    Object.keys(data.print || {}).forEach(function (k) {
      var d = data.print[k];
      var label = printLabel(data, k) || k;
      var parts = [];
      if (d.position) parts.push(d.position);
      if (d.mode === 'text') {
        if (d.text) parts.push('"' + oneLine(d.text) + '"');
        if (d.font) parts.push(d.font);
        if (d.thread) parts.push(d.thread + ' thread');
      }
      if (d.mode === 'image' && d.image) parts.push('image uploaded');
      bits.push(label + (parts.length ? ' — ' + parts.join(', ') : ''));
    });

    Object.keys(data.fields || {}).forEach(function (k) {
      var v = data.fields[k];
      if (!v) return;
      if (/^https?:/i.test(v)) bits.push(k + ': uploaded');
      else bits.push(k + ': ' + (isDateValue(v) ? ukDate(v) : oneLine(v)));
    });

    var det = document.createElement('p');
    det.className = 'bb-review__detail';
    det.textContent = bits.length ? bits.join(' · ') : 'Nothing chosen yet';
    li.appendChild(det);

    if (jumpTo !== null && jumpTo !== undefined) {
      var ed = document.createElement('button');
      ed.type = 'button';
      ed.className = 'bb-review__edit';
      ed.textContent = 'Edit this';
      ed.addEventListener('click', function () {
        idx = jumpTo;
        editingExtra = null;
        render();
      });
      li.appendChild(ed);
    }

    return li;
  }

  /* The review and the cart both need a location's shopper-facing label from
     its key, and the key alone does not carry it. */
  function printLabel(data, key) {
    var member = data.handle ? memberByHandle(data.handle) : null;
    if (!member || !member.print) return null;
    var out = null;
    (member.print.locations || []).forEach(function (l) {
      if (l.key === key) out = l.label;
    });
    return out;
  }

  /* --------------------------------------------------------------- render */

  /* Which screen was drawn last time, so render() can tell a move to a new
     step from a redraw of the one already on screen. */
  var lastScreen = null;

  function render() {
    var step = steps[idx];
    if (!step) return;

    /* Every tap redraws the whole screen, which used to throw the shopper
       back to the top: pick a colour near the bottom of a long list and the
       panel you were working in jumped out of sight. On the personalisation
       step the text box sits below the fold, so after choosing "Text" the
       field appeared to vanish entirely.

       A new step still starts at the top, as it should. A redraw of the same
       step keeps where the shopper was. */
    var screen = idx + '|' + step.kind + '|' + (step.key || '') + '|' + editingExtra;
    var keep = screen === lastScreen ? els.body.scrollTop : 0;
    lastScreen = screen;

    els.err.textContent = '';
    els.body.classList.remove('bb-invalid');

    if (step.kind === 'slot') renderSlot(step);
    else if (step.kind === 'grid') renderGrid(step);
    else if (step.kind === 'custom') renderCustom(step);
    else if (step.kind === 'extras') renderExtras();
    else renderReview();

    els.back.style.visibility = (idx === 0 && editingExtra === null) ? 'hidden' : 'visible';
    els.bar.style.width = Math.round(((idx + 1) / steps.length) * 100) + '%';
    els.total.textContent = money(total());

    /* Every other step clears it; without this a button disabled during an
       upload stays dead once the upload finishes. */
    els.next.disabled = false;
    applyUploadUI();

    if (pendingError) {
      els.err.textContent = pendingError;
      els.body.classList.add('bb-invalid');
      pendingError = null;
    }

    /* Put the scroll back. Once is not enough — the content has only just
       been rebuilt, so the browser clamps anything past the height it knows
       about, and that height grows again as the garment photos arrive. */
    els.body.scrollTop = keep;
    if (keep > 0) {
      var put = function () { els.body.scrollTop = keep; };
      requestAnimationFrame(put);
      setTimeout(put, 60);
      Array.prototype.forEach.call(els.body.querySelectorAll('img'), function (im) {
        if (!im.complete) im.addEventListener('load', put, { once: true });
      });
    }
  }

  /* ------------------------------------------------------------ validation */

  function checkGarment(member, data) {
    var product = products[member.handle];
    if (!product) return 'This item could not be loaded. Please refresh and try again.';

    if (optionIndex(product, 'colour') >= 0 && !data.opts.colour) return 'Choose a colour.';
    if (optionIndex(product, 'size') >= 0 && !data.opts.size) return 'Choose a size.';

    return checkPersonalisation(member, data);
  }

  function checkPersonalisation(member, data) {
    var missingWant = null;
    /* Only the questions the admin marked with a label are demanded; a bare
       "Text" line is treated as optional, as it is on the product page. */
    wantsOf(member, 'text').forEach(function (w) {
      if (missingWant || !w.label) return;
      if (!String(data.fields[w.label] || '').trim()) {
        missingWant = 'Please fill in ' + w.label + '.';
      }
    });
    if (missingWant) return missingWant;

    var imgWant = wantsOf(member, 'image')[0];
    if (imgWant && imgWant.label && !(member.inline && member.inline.hasOldUploader)) {
      if (!data.fields[imgWant.label]) return 'Please add your ' + imgWant.label.toLowerCase() + '.';
    }

    if (!member.print) return null;

    var locs = availableLocations(member);
    if (!locs.length) return null;

    var chosen = Object.keys(data.print || {});
    if (!chosen.length) return 'Choose where you would like it printed.';

    var missing = null;
    locs.forEach(function (l) {
      if (missing) return;
      var d = data.print[l.key];
      if (!d) return;

      if (usesFrontPositions(member, l) && !d.position) {
        missing = 'Choose a position for the ' + l.label + ' print.';
        return;
      }
      if (!d.mode) {
        missing = 'Choose text or an image for the ' + l.label + ' print.';
        return;
      }
      if (d.mode === 'text') {
        if (!String(d.text || '').trim()) { missing = 'Add the text for the ' + l.label + ' print.'; return; }
        if (FONTS.length && !d.font) { missing = 'Choose a font for the ' + l.label + ' print.'; return; }
        if (THREADS.length && !d.thread) { missing = 'Choose a thread colour for the ' + l.label + ' print.'; return; }
      }
      if (d.mode === 'image' && !d.image) {
        missing = 'Add your image for the ' + l.label + ' print.';
      }
    });
    return missing;
  }

  function validate() {
    var step = steps[idx];

    if (step.kind === 'slot') {
      return checkGarment(step.member, slotState(step.key));
    }

    if (step.kind === 'grid') {
      var want = memberQty(step.member);
      var have = gridCount(gridState(step.member));
      if (have === want) return null;
      if (have < want) {
        return 'Choose ' + (want - have) + ' more — the bundle is ' + want + ' items.';
      }
      return 'That is ' + (have - want) + ' too many — the bundle is ' + want + ' items.';
    }

    if (step.kind === 'custom') {
      return checkPersonalisation(step.member, gridState(step.member));
    }

    if (step.kind === 'extras' && editingExtra !== null) {
      var x = state.extras[editingExtra];
      var member = memberByHandle(x.handle);
      if (!member) return 'This item could not be loaded.';
      var err = checkGarment(member, x);
      if (err) return err;
      x.variant = findVariant(products[x.handle], x.opts);
      if (!x.variant) return 'That combination is not available — please pick another size or colour.';
      return null;
    }

    return null;
  }

  /* ------------------------------------------------------------ add to cart */

  /* Written so the print team can read an order straight off the line items
     without opening anything else. */
  function propsFor(member, data, prefix) {
    var out = {};
    var product = products[member.handle];
    /* A grid garment has no single size or colour of its own — its picks map
       carries them instead — so these two are simply absent there. */
    var opts = data.opts || {};

    if (product && optionIndex(product, 'colour') >= 0 && opts.colour) {
      out[prefix + 'Colour'] = opts.colour;
    }
    if (product && optionIndex(product, 'size') >= 0 && opts.size) {
      out[prefix + 'Size'] = opts.size;
    }

    /* A run of ten reads as one line per colour — "Black: M x3, L x4" —
       rather than ten identical properties. A garment with no sizes gives
       the warehouse the plain count instead: "Tote Bag — Black: 3". */
    Object.keys((data.picks) || {}).forEach(function (c) {
      var sizes = Object.keys(data.picks[c]).map(function (sz) {
        var n = data.picks[c][sz];
        return sz ? sz + ' x' + n : String(n);
      });
      out[prefix + (c || 'Quantity')] = sizes.join(', ');
    });

    availableLocations(member).forEach(function (l) {
      var d = data.print && data.print[l.key];
      if (!d) return;
      var base = prefix + l.label;
      if (d.position) out[base + ' position'] = d.position;
      if (d.mode === 'text') {
        out[base + ' text'] = d.text || '';
        if (d.font) out[base + ' font'] = d.font;
        if (d.thread) out[base + ' thread'] = d.thread;
      }
      if (d.mode === 'image' && d.image) out[base + ' artwork'] = d.image;
    });

    Object.keys(data.fields || {}).forEach(function (k) {
      var v = data.fields[k];
      if (!v) return;
      out[prefix + k] = isDateValue(v) ? ukDate(v) : v;
    });

    return out;
  }

  /* A paid print location has to be its own line, for the same reason a paid
     extra does: the price lives on the Additional Print Location variant and a
     storefront cannot override it. */
  function printLines(member, data, forLabel, bundleId, qty) {
    var lines = [];
    var n = Math.max(1, Number(qty) || 1);
    availableLocations(member).forEach(function (l) {
      var d = data.print && data.print[l.key];
      if (!d) return;
      if (!(Number(l.price) > 0) || !l.variantId) return;
      lines.push({
        id: Number(l.variantId),
        quantity: n,
        properties: {
          _bundle_of: bundleId,
          'Print location': l.label,
          For: forLabel
        }
      });
    });
    return lines;
  }

  function slimState() {
    var slots = {};
    var grids = {};
    steps.forEach(function (st) {
      if (st.kind === 'slot') {
        var d = state.slots[st.key];
        if (d) slots[st.key] = { opts: d.opts, fields: d.fields, print: d.print };
      } else if (st.kind === 'grid') {
        var g = state.grids[st.member.handle];
        if (g) grids[st.member.handle] = { picks: g.picks, fields: g.fields, print: g.print };
      }
    });
    return JSON.stringify({
      slots: slots,
      grids: grids,
      extras: state.extras.map(function (x) {
        return {
          handle: x.handle, opts: x.opts, fields: x.fields, print: x.print,
          variantId: x.variant ? x.variant.id : null,
          price: x.variant ? x.variant.price : 0
        };
      })
    });
  }

  function addToCart() {
    if (busy) return;
    busy = true;
    els.next.disabled = true;
    els.next.textContent = 'Adding…';

    var bundleId = 'bn-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

    var props = {};
    var extraLines = [];

    steps.forEach(function (st) {
      if (st.kind === 'slot') {
        var data = slotState(st.key);
        var p = propsFor(st.member, data, st.label + ' — ');
        Object.keys(p).forEach(function (k) { props[k] = p[k]; });
        extraLines = extraLines.concat(printLines(st.member, data, st.label, bundleId, 1));
        return;
      }
      if (st.kind === 'grid') {
        var g = gridState(st.member);
        var qty = memberQty(st.member);
        var gp = propsFor(st.member, g, st.label + ' — ');
        Object.keys(gp).forEach(function (k) { props[k] = gp[k]; });
        /* One print line for the whole run, at quantity ten, rather than ten
           separate lines saying the same thing. */
        extraLines = extraLines.concat(printLines(st.member, g, st.label, bundleId, qty));
      }
    });

    props._bundle_id = bundleId;
    /* A compact copy. The full variant object is only needed while the modal
       is open; carrying it into every cart and order makes the property
       several times longer for nothing. */
    props._bundle_state = slimState();

    var items = [{ id: Number(cfg.variantId), quantity: 1, properties: props }];

    state.extras.forEach(function (x) {
      var member = memberByHandle(x.handle);
      if (!member || !x.variant) return;
      var product = products[x.handle];
      var label = product ? product.title : member.label;
      var ap = propsFor(member, x, '');
      ap._bundle_of = bundleId;
      ap['Part of'] = cfg.productTitle;
      items.push({ id: Number(x.variant.id), quantity: 1, properties: ap });
      extraLines = extraLines.concat(printLines(member, x, label, bundleId, 1));
    });

    items = items.concat(extraLines);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (e) {
            throw new Error(e.description || e.message || 'Could not add to cart');
          });
        }
        return r.json();
      })
      .then(function () { window.location.href = '/cart'; })
      .catch(function (err) {
        busy = false;
        els.next.disabled = false;
        els.next.textContent = 'Add to cart';
        els.err.textContent = err.message || 'Could not add to cart. Please try again.';
      });
  }

  /* ------------------------------------------------------------ open/close */

  function open() {
    els.scrim.hidden = false;
    els.modal.hidden = false;
    document.body.classList.add('bb-locked');

    els.body.innerHTML = '<p class="bb-loading">Loading…</p>';
    loadFonts();

    /* Variants are fetched now rather than printed into the page: six garments
       at 140 variants each would be over 100KB of HTML on a page most shoppers
       never open the builder on. */
    var handles = MEMBERS.map(function (m) { return m.handle; });
    Promise.all(handles.map(fetchProduct)).then(function () {
      buildSteps();
      idx = 0;
      editingExtra = null;
      render();
    });
  }

  function close() {
    els.scrim.hidden = true;
    els.modal.hidden = true;
    document.body.classList.remove('bb-locked');
  }

  /* --------------------------------------------------------------- button */

  function swapButton() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var done = false;

    Array.prototype.forEach.call(forms, function (form) {
      var subs = form.querySelectorAll('.product-form__submit:not(.bb-trigger)');
      if (!subs.length) return;

      /* Every real submit goes — the main form, its duplicate and the sticky
         bar. Leaving one visible lets a shopper buy the bundle with nobody's
         details filled in. */
      Array.prototype.forEach.call(subs, function (s) {
        s.hidden = true;
        s.style.display = 'none';
      });

      if (!form.querySelector('.bb-trigger')) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = subs[0].className + ' bb-trigger';
        btn.innerHTML = '<span>Build your bundle</span>';
        btn.setAttribute('data-bb-event', 'open_bundle');
        btn.setAttribute('data-bb-product-id', cfg.productId);
        btn.addEventListener('click', open);
        subs[0].parentNode.insertBefore(btn, subs[0]);
      }

      var dyn = form.querySelector('.product-form__buttons--payment, .shopify-payment-button');
      if (dyn) dyn.style.display = 'none';

      done = true;
    });

    if (done) document.body.classList.add('bb-ready');
    return done;
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    var node = document.querySelector('script[id^="BundleConfig-"]');
    if (!node) return;

    try { cfg = JSON.parse(node.textContent); } catch (e) { return; }
    if (!cfg || !cfg.members || !cfg.members.length) return;

    MEMBERS = cfg.members;
    THREADS = parseColours(cfg.threadColours);
    FONTS = parseFonts(cfg.fonts);

    els.modal = document.querySelector('[data-bb="modal"]');
    els.scrim = document.querySelector('[data-bb="scrim"]');
    if (!els.modal || !els.scrim) return;

    ['title', 'sub', 'bar', 'body', 'back', 'next', 'close', 'err', 'total'].forEach(function (k) {
      els[k] = els.modal.querySelector('[data-bb="' + k + '"]');
    });

    els.close.addEventListener('click', close);
    /* Clicking the backdrop does not close it. On a bundle that takes six
       screens to fill in, one stray tap would throw all of it away. */

    els.back.addEventListener('click', function () {
      if (editingExtra !== null) {
        /* Backing out of a half-filled extra removes it rather than leaving a
           blank row behind. */
        var x = state.extras[editingExtra];
        if (x && !x.variant) state.extras.splice(editingExtra, 1);
        editingExtra = null;
        render();
        return;
      }
      if (idx > 0) { idx--; render(); }
    });

    els.next.addEventListener('click', function () {
      var err = validate();
      if (err) { pendingError = err; render(); return; }

      if (editingExtra !== null) { editingExtra = null; render(); return; }
      if (steps[idx].kind === 'review') { addToCart(); return; }
      if (idx < steps.length - 1) { idx++; render(); }
    });

    /* Bounded retry for the first paint — the product form can arrive after
       this script on a slow connection. */
    var tries = 0;
    var iv = setInterval(function () {
      if (swapButton() || ++tries > 40) clearInterval(iv);
    }, 150);

    /* product-info.js replaces the form on variant change, taking our button
       with it. */
    var host = document.querySelector('product-info, .product__info-container');
    if (host) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; swapButton(); }, 120);
      }).observe(host, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();